import { useEffect, useState } from 'react';
import { X, Eye, MousePointerClick, PencilLine, Send, Download, Heart, Share2, Play, CheckCircle2, Activity } from 'lucide-react';
import { getEvents, type EngagementRow, type TrackingEventRow } from '../services/magnetPortalService';

const EVENT_ICON: Record<string, any> = {
  VIEW: Eye, OPEN: Eye, CLICK: MousePointerClick, FORM_VIEW: Eye,
  FORM_START: PencilLine, FORM_SUBMIT: Send, DOWNLOAD: Download, LIKE: Heart,
  SHARE: Share2, VIDEO_PLAY: Play, VIDEO_COMPLETE: CheckCircle2, ASSESSMENT_COMPLETE: CheckCircle2,
};

export default function EventTimelineDrawer({
  row, onClose,
}: {
  row: EngagementRow | null;
  onClose: () => void;
}) {
  const [events, setEvents] = useState<TrackingEventRow[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!row) return;
    setLoading(true);
    getEvents({ magnetDeliveryId: row.deliveryId, take: 200 })
      .then((res) => setEvents(res.data))
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, [row]);

  if (!row) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-md bg-card border-l border-border h-full overflow-y-auto shadow-xl">
        <div className="sticky top-0 bg-card border-b border-border px-5 py-4 flex items-start justify-between">
          <div>
            <div className="text-sm font-semibold text-foreground">
              {row.contact.firstName} {row.contact.lastName}
            </div>
            <div className="text-xs text-muted-foreground">{row.magnet.title}</div>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground" aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-1 border-b border-border">
          <Meta label="Email" value={row.contact.email || '—'} />
          <Meta label="Account" value={row.contact.account?.name || '—'} />
          <Meta label="Title" value={row.contact.title || '—'} />
          <Meta label="Engagement" value={`${row.contact.engagementScore} · ${row.contact.status}`} />
          <Meta label="Total events" value={String(row.eventCount)} />
        </div>

        <div className="px-5 py-4">
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Event timeline</h4>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
            </div>
          ) : events.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4">No raw events recorded.</p>
          ) : (
            <ol className="space-y-3">
              {events.map((e) => {
                const Icon = EVENT_ICON[e.eventType] || Activity;
                return (
                  <li key={e.id} className="flex gap-3">
                    <div className="mt-0.5 h-7 w-7 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0">
                      <Icon className="h-3.5 w-3.5" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-foreground">{e.eventType.replace(/_/g, ' ')}</div>
                      <div className="text-xs text-muted-foreground">
                        {new Date(e.createdAt).toLocaleString()}
                      </div>
                      {e.eventData && (
                        <div className="text-xs text-muted-foreground/80 mt-0.5 truncate">
                          {JSON.stringify(e.eventData)}
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      </div>
    </div>
  );
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-foreground text-right truncate">{value}</span>
    </div>
  );
}
